import * as React from 'react';
import { useMemo } from 'react';

import { Box, Switch, FormControlLabel, Typography } from '@mui/material';

import { EntityMetadataModel, EntityPropertyMetadataModel, ParameterMetadata, ParameterType, PrimitiveType } from '../../../../api/api';
import { useExpressionMetadata } from '../../../../components/ExpressionMetadataContext';
import { CollectionMapExpressionModel } from '../../Models/CollectionMapExpressionModel';
import { ExpressionModel } from '../../Models/ExpressionModel';
import { ExpressionTypes } from '../../Models/ExpressionTypes';
import { ObjectExpressionModel } from '../../Models/ObjectExpressionModel';
import { ChildVariableScope } from '../../Models/VariableScopes/ChildVariableScope';
import { IVariableScope } from '../../Models/VariableScopes/IVariableScope';
import { VariableScope } from '../../Models/VariableScopes/VariableScope';
import { ExpressionCopyPasteActions } from './ExpressionCopyPasteActions';
import ExpressionObjectPlaceholder from './ExpressionObjectPlaceholder';
import ExpressionPlaceholder from './ExpressionPlaceholder';
import MapIfExpressionControl from './MapIfExpressionControl';
import { getCaseInsensitivePropertyValue } from './caseInsensitiveProperty';
import { getObjectExpression } from './getObjectExpression';
import { createObjectMapExpression, updateCollectionMapExpression, updateObjectMapExpression } from './mapExpressionHelpers';
import { shouldShowObjectEditor } from './shouldShowObjectEditor';

export type ExpressionObjectNestedPlaceholderProps = {
	property: EntityPropertyMetadataModel;
	expression: ObjectExpressionModel | undefined;
	onExpressionChange?: (expression: ObjectExpressionModel | undefined) => void;
	expectedObjectType: EntityMetadataModel;
	variableScope: IVariableScope | undefined;
};

// Component implementations
export default function ExpressionObjectNestedPlaceholder({
	property,
	expression,
	onExpressionChange,
	expectedObjectType,
	variableScope,
}: ExpressionObjectNestedPlaceholderProps) {
	const { expressionScopeCalculator } = useExpressionMetadata();
	const propertyExpression = getCaseInsensitivePropertyValue<ExpressionModel>(expression?.Properties, property.name);

	const nestedVariableScope = useMemo(() => {
		if (property.isMultiple && property.referenceEntity) {
			if (!variableScope) {
				return variableScope;
			}

			// Determine the $item type by analyzing the Collection expression
			let itemIsMultiple = false;
			let itemReferenceEntity = property.referenceEntity;

			if (propertyExpression?.Type === ExpressionTypes.CollectionMap) {
				const collectionMapExpr = propertyExpression as CollectionMapExpressionModel;
				const collectionExpr = collectionMapExpr.Collection;

				if (collectionExpr && variableScope && expressionScopeCalculator) {
					// Use ExpressionVariableScopeCalculatorService to analyze and unwrap the Collection expression's type
					const collectionType = expressionScopeCalculator.analyzeExpressionType(collectionExpr, variableScope);
					const unwrappedType = expressionScopeCalculator.unwrapCollectionType(collectionType);

					if (unwrappedType) {
						itemIsMultiple = unwrappedType.isMultiple || false;
						itemReferenceEntity = unwrappedType.referenceEntity || property.referenceEntity;
					}
				}
			}

			return new ChildVariableScope(variableScope as VariableScope, {
				name: '$item',
				type: PrimitiveType.Object,
				isComplex: true,
				isMultiple: itemIsMultiple,
				isRequired: true,
				referenceEntity: itemReferenceEntity,
			});
		}
		return variableScope;
	}, [property.isMultiple, property.referenceEntity, propertyExpression, variableScope, expressionScopeCalculator]);

	const collectionParameterMetadata = useMemo(() => {
		return {
			name: property.name,
			displayName: 'Map From',
			type: ParameterType.Expression,
			categories: [],
			expectedType: { type: PrimitiveType.Collection },
			order: 0,
			isCollection: true,
			isRequired: false,
		} as ParameterMetadata;
	}, [property.name]);

	const nestedExpression = useMemo(() => {
		if (!propertyExpression) return undefined;
		if (property.isMultiple && propertyExpression.Type === ExpressionTypes.CollectionMap) {
			return (propertyExpression as CollectionMapExpressionModel).Map as ExpressionModel | undefined;
		}
		return propertyExpression as ExpressionModel | undefined;
	}, [propertyExpression, property.isMultiple]);

	const nestedParameterMetadata = useMemo(() => {
		return {
			name: property.name,
			displayName: property.name,
			type: ParameterType.Expression,
			categories: [],
			expectedType: {
				type: property.type,
				isCollection: false,
				referenceEntity: property.referenceEntity,
			},
			order: 0,
			isCollection: false,
			isRequired: false,
		} as ParameterMetadata;
	}, [property.name, property.type, property.referenceEntity]);

	// Use consistent logic for determining when to show object editor
	const isObjectType = !!property.referenceEntity;
	const nestedExpressionType = nestedExpression?.Type;
	const shouldShowObjectPlaceholder = shouldShowObjectEditor(nestedExpression, nestedExpressionType, isObjectType);

	const mapIfExpression =
		property.isMultiple && propertyExpression?.Type === ExpressionTypes.CollectionMap
			? (propertyExpression as CollectionMapExpressionModel).MapIf
			: (propertyExpression as ObjectExpressionModel | undefined)?.MapIf;

	const handleMapIfExpressionChange = (mapIfExpression: ExpressionModel | undefined) => {
		let finalPropertyExpression: ExpressionModel | undefined;
		if (property.isMultiple) {
			finalPropertyExpression = updateCollectionMapExpression(propertyExpression as CollectionMapExpressionModel | undefined, { mapIf: mapIfExpression });
		} else {
			finalPropertyExpression = updateObjectMapExpression(propertyExpression as ObjectExpressionModel | undefined, { mapIf: mapIfExpression });
		}

		const newExpression = getObjectExpression(expression, property, finalPropertyExpression, expectedObjectType);
		onExpressionChange?.(newExpression);
	};

	return (
		<>
			<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, px: 1, py: 0.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
				<FormControlLabel
					control={
						<Switch
							size="small"
							checked={propertyExpression !== undefined}
							onChange={(e) => {
								let newPropertyExpression: ExpressionModel | undefined;
								if (e.target.checked) {
									if (property.isMultiple) {
										// Create CollectionMap with Map parameter containing an ObjectExpression
										newPropertyExpression = updateCollectionMapExpression(undefined, { map: createObjectMapExpression() });
									} else {
										newPropertyExpression = createObjectMapExpression();
									}
								} else {
									newPropertyExpression = undefined;
								}
								const newExpression = getObjectExpression(expression, property, newPropertyExpression, expectedObjectType);
								onExpressionChange?.(newExpression);
							}}
						/>
					}
					label={property.isMultiple ? 'Map Objects' : 'Map Object'}
					sx={{ mr: 'auto', '& .MuiFormControlLabel-label': { fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' } }}
				/>
				<ExpressionCopyPasteActions
					expression={propertyExpression}
					onPaste={
						onExpressionChange
							? (pastedExpr) => {
									const newExpression = getObjectExpression(expression, property, pastedExpr, expectedObjectType);
									onExpressionChange(newExpression);
								}
							: undefined
					}
					keyboardPaste
				/>
			</Box>
			{propertyExpression !== undefined && (
				<Box
					sx={{
						mt: 1,
						mb: 1,
						p: 1,
						minWidth: 0,
						bgcolor: 'background.default',
						border: 1,
						borderColor: 'divider',
						borderLeft: '3px solid',
						borderLeftColor: 'primary.main',
						borderRadius: 1,
					}}
				>
					<MapIfExpressionControl expression={mapIfExpression} onExpressionChange={handleMapIfExpressionChange} variableScope={variableScope} />
					{property.isMultiple && (
						<Box sx={{ mb: 1 }}>
							<Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
								Map From
							</Typography>
							<ExpressionPlaceholder
								variableScope={variableScope}
								parameterMetadata={collectionParameterMetadata}
								parameterEnum={undefined}
								expression={(propertyExpression as CollectionMapExpressionModel)?.Collection}
								onExpressionChange={(collectionExpression) => {
									const finalPropertyExpression = updateCollectionMapExpression(propertyExpression as CollectionMapExpressionModel | undefined, {
										collection: collectionExpression,
									});
									const newExpression = getObjectExpression(expression, property, finalPropertyExpression, expectedObjectType);
									onExpressionChange?.(newExpression);
								}}
								isEditable={true}
							/>
						</Box>
					)}
					{property.isMultiple && (
						<Typography variant="body2" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
							Each element as:
						</Typography>
					)}
					{shouldShowObjectPlaceholder ? (
						<ExpressionObjectPlaceholder
							expression={nestedExpression as ObjectExpressionModel | undefined}
							onExpressionChange={(updatedObjectExpression) => {
								let finalPropertyExpression: ExpressionModel | undefined;
								if (property.isMultiple) {
									// Wrap back in CollectionMap, preserving Collection if it exists
									finalPropertyExpression = updateCollectionMapExpression(propertyExpression as CollectionMapExpressionModel | undefined, {
										map: updatedObjectExpression,
									});
								} else {
									finalPropertyExpression = updatedObjectExpression;
								}
								const newExpression = getObjectExpression(expression, property, finalPropertyExpression, expectedObjectType);
								onExpressionChange?.(newExpression);
							}}
							variableScope={nestedVariableScope}
							expectedObjectType={property.referenceEntity}
						/>
					) : (
						<ExpressionPlaceholder
							variableScope={nestedVariableScope}
							parameterMetadata={nestedParameterMetadata}
							parameterEnum={undefined}
							expression={nestedExpression}
							onExpressionChange={(updatedExpression) => {
								let finalPropertyExpression: ExpressionModel | undefined;
								if (property.isMultiple) {
									// Wrap back in CollectionMap, preserving Collection if it exists
									finalPropertyExpression = updateCollectionMapExpression(propertyExpression as CollectionMapExpressionModel | undefined, {
										map: updatedExpression,
									});
								} else {
									finalPropertyExpression = updatedExpression;
								}
								const newExpression = getObjectExpression(expression, property, finalPropertyExpression, expectedObjectType);
								onExpressionChange?.(newExpression);
							}}
							isEditable={true}
							expectedObjectType={property.referenceEntity}
						/>
					)}
				</Box>
			)}
		</>
	);
}
